import { debounce } from 'underscore';
import { Icon } from 'js/components/common/Icon';
import Overlay from 'js/components/common/Overlay';
import { InputShallow } from 'js/components/common/Fields/Input';
import React, { Fragment, useEffect, useState } from 'react';
import { useDeviceSelector, useGameConfigurationSelector, useUserSelector } from 'js/store/hooks';
import { mapProps, onlyUpdateForKeys, shouldUpdate, withHandlers, withProps } from 'recompose';
import { compose, equals, omit, pick, prop } from 'ramda';
import { trackGameSearchChange } from 'js/components/main/Games/trackingApi';
import style from './index.scss';
import { useTranslations } from 'js/hooks/i18n';
import { useAvailableGames } from '../hooks/useAvailableGames';
import { GameItem } from './GameItem';
import { GameItemBody } from './GameItemBody';
import { highlightMatches, setSearchGamesFn } from './getFormatValues';

export const SearchBare = ({
    query,
    result,
    isActive,
    onFocus,
    handleBlur,
    onChange,
    handleBack,
    handleReset,
    setSearchContainerRef,
    setSearchInputRef,
    className = '',
    activeClassName = ''
}) => {
    const activeCls = isActive ? `${style.active} ${activeClassName}` : '';
    const clsName = `${style.searchContainer} ${className} ${activeCls}`;
    const t = useTranslations();

    return (
        <Fragment>
            {isActive && <Overlay className={style.overlay} />}
            <div className={clsName} ref={setSearchContainerRef}>
                <div className={style.inputWrapper}>
                    <div className={`${style.inputContainer} ${result.length > 0 && style.isResult}`}>
                        {isActive && (
                            <Icon type="back_arrow"
                                  onClick={handleBack}
                                  className={`${style.icon} ${style.back}`}
                            />
                        )}
                      <InputShallow placeholder={t('playtech.mobile.system.stickySearch.placeholder')}
                                                   value={query}
                                                   onClick={onFocus}
                                                   onChange={onChange}
                                                   ref={setSearchInputRef}
                                                   className={style.input}
                        />
                        {isActive && query.length > 0 && (
                            <Icon type="close_svg"
                                  onClick={handleReset}
                                  className={`${style.icon} ${style.close}`}
                            />
                        )}
                        {isActive && result.length > 0 && (
                            <div className={style.counter}>
                                {result.length} {t(`${result.length > 1 ? t('playtech.mobile.system.stickySearch.items') : t('playtech.mobile.system.stickySearch.item')}`)}
                            </div>
                        )}
                    </div>
                </div>

                {isActive && <ul className={`${style.list} ${isActive && result.length > 0 ? style.shown : ''}`}>
                    {result && result.map(({ id, value, source: { image }, matchesStart, matchesEnd }) => (
                        <GameItem key={id} code={id} name={value} handleBlur={handleBlur}>
                            <GameItemBody
                                gameCode={id}
                                image={image}
                                value={value}
                                imageClassName={style.image}
                                matchesStart={matchesStart}
                                matchesEnd={matchesEnd}
                                highlightMatches={highlightMatches}
                            />
                        </GameItem>
                    ))}
                </ul> }
            </div>
        </Fragment>
    );
};

export const Search = compose(
    onlyUpdateForKeys(['query', 'result', 'isActive']),
    withProps(() => {
        const isLoggedIn = useUserSelector(prop('isLoggedIn'));
        const games = useGameConfigurationSelector(prop('games'));
        const [searchGames, setSearchGames] = useState(null);
        const [query, setQuery] = useState('');
        const [result, setResult] = useState([]);
        const [isActive, setIsActive] = useState(false);
        const availableGames = useAvailableGames();

        useEffect(() => {
            setSearchGamesFn({
                games: availableGames,
                setSearchGames
            });
        }, [games, isLoggedIn]);

        return {
            ...useDeviceSelector(pick(['os'])),

            searchGames,
            query, setQuery,
            result, setResult,
            isActive, setIsActive
        };
    }),
    shouldUpdate((props, { query, result, isActive, gameCodes }) =>
        props.query !== query ||
        props.result !== result ||
        props.isActive !== isActive ||
        !equals(props.gameCodes, gameCodes)
    ),
    withHandlers(({
                      feedName,
                      setIsActive
                  }) => {
        let containerRef;
        let inputRef;
        const setInputFocus = () => inputRef.focus();
        const setFocusOfTheEnd = () => inputRef.selectionStart = inputRef.value.length;
        const setIsActiveWrapper = (isActive) => {
            document.documentElement.classList.toggle(style.hideNavigationBar, isActive);
            setIsActive(isActive);
        };

        const handleBlur = ({ target }) => {
            if (!containerRef.contains(target) || target === containerRef) {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
            }
        };

        const handleExternalAPI = debounce((search) => {
            trackGameSearchChange('reactPortletId', feedName, search);
        }, 1500);

        return {
            setSearchContainerRef: () => ref => containerRef = ref,
            setSearchInputRef: () => ref => inputRef = ref,
            setFocus: () => () => setInputFocus(),
            handleBlur: () => () => {
                setIsActiveWrapper(false);
                window.removeEventListener('click', handleBlur);
            },
            onFocus: ({ os }) => () => {
                setIsActiveWrapper(true);
                window.scrollTo(0, 0);
                setTimeout(() => window.addEventListener('click', handleBlur));
                setFocusOfTheEnd();
                const isNeedDelay = os === 'ios';

                if (isNeedDelay) {
                    setTimeout(() => scrollTo(0, 0), 300);
                }
            },
            onChange: ({ setQuery, setResult, searchGames }) => (event) => {
                const { value } = event.target;
                setQuery(value);
                value.length > 1 ? setResult(searchGames(value.trim())) : setResult([]);
                handleExternalAPI(value.trim());
            },
            handleReset: ({ setQuery, setResult }) => (event) => {
                event.stopPropagation();
                setQuery('');
                setResult([]);
                setInputFocus();
            },
            handleBack: ({ setQuery, setResult }) => (event) => {
                event.stopPropagation();
                setQuery('');
                setResult([]);
                setIsActiveWrapper(false);
                removeEventListener('click', handleBlur);
            }
        };
    }),
    mapProps(omit([
        'games',
        'gameCodes',
        'feedName',
        'setQuery',
        'setResult',
        'setIsActive',
        'searchGames',
        'setSearchGames'
    ]))
)(SearchBare);
